import {
  Component,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';

import { User } from '../manage-access.model';

import { FILTER_GROUP, USER_LOADING_STAGE } from '../manage-access.constant';

import { DialogUserViewAccessComponent } from '../dialog-user-view-access/dialog-user-view-access.component';
import { DialogUserRemoveAccessComponent } from '../dialog-user-remove-access/dialog-user-remove-access.component';
import { DialogUserResetPasswordComponent } from '../dialog-user-reset-password/dialog-user-reset-password.component';
import { LOB_DENTAL } from '@cigna/chcp/auth/util';

@Component({
  selector: 'cigna-user-overview-table',
  templateUrl: './user-overview-table.component.html',
  styleUrls: ['./user-overview-table.component.scss'],
})
export class UserOverviewTableComponent implements OnChanges {
  sortedData: User[] = [];

  @Input() users: User[] = [];

  @Input() stage: USER_LOADING_STAGE;

  @Input() amUserId: string;
  @Input() amLob: string;

  @Input() filterText = '';
  @Input() filterGroup = '';
  @Input() isImpersonator = false;

  @Output() userSelected = new EventEmitter();
  @Output() usersToggleAll = new EventEmitter();
  @Output() userSelectAndEdit = new EventEmitter();
  @Output() userGetEntitlements = new EventEmitter();

  @ViewChild('tableElement', { read: ElementRef, static: true })
  private tableElement: ElementRef;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  FILTER_GROUP = FILTER_GROUP;
  USER_LOADING_STAGE = USER_LOADING_STAGE;
  LOB_DENTAL = LOB_DENTAL;

  constructor(public dialog: MatDialog) {}

  ngOnChanges() {
    this.sortedData = this.users;
    if (
      this.sortedData.length > 0 &&
      (this.stage === USER_LOADING_STAGE.loaded_all ||
        this.stage === USER_LOADING_STAGE.loaded_pending)
    ) {
      this.selectAllCheck(); // not sure best place for this...
    }
  }

  openUserAccessDialog(user: User) {
    // open the modal
    const dialogRef = this.dialog.open(DialogUserViewAccessComponent, {
      data: {
        user,
        showEdit: true,
        isLoading: true,
      },
      autoFocus: false,
      width: '800px',
    });

    dialogRef.componentInstance.editIndividualUser.subscribe(() => {
      this.userSelectAndEdit.emit(user);
      dialogRef.close();
    });
  }

  openUserRemoveAccessDialog(user: User) {
    this.dialog.open(DialogUserRemoveAccessComponent, {
      data: {
        user,
      },
      autoFocus: false,
      disableClose: true,
      width: '700px',
    });
  }

  openResetUserPasswordDialog(user: User) {
    this.dialog.open(DialogUserResetPasswordComponent, {
      data: {
        user,
      },
      autoFocus: false,
      disableClose: true,
      width: '700px',
    });
  }

  selectUser(user: User, event: any): void {
    const checked = event.target.checked;

    this.userSelected.emit({
      checked,
      user,
    });

    this.selectAllCheck();
  }

  selectAllCheck(): void {
    const rowsTotal = this.users.length;
    const headerCheckbox =
      this.tableElement.nativeElement.querySelector('thead th input');
    const rowsChecked = this.tableElement.nativeElement.querySelectorAll(
      'tbody tr input:checked',
    );

    if (headerCheckbox === null) {
      return;
    }

    if (
      rowsChecked.length === rowsTotal &&
      rowsTotal !== 0 &&
      !headerCheckbox.checked
    ) {
      headerCheckbox.checked = true;
    }
    if (
      (rowsChecked.length !== rowsTotal || rowsTotal === 0) &&
      headerCheckbox.checked
    ) {
      headerCheckbox.checked = false;
    }
  }

  selectAllUsers(event: any): void {
    const checked = event.target.checked;

    // set checkbox state in table
    const rowCheckboxes =
      this.tableElement.nativeElement.querySelectorAll('tbody tr input');
    if (checked) {
      rowCheckboxes.forEach((input: HTMLInputElement) => {
        if (!input.checked) {
          input.checked = true;
        }
      });
    } else {
      rowCheckboxes.forEach((input: HTMLInputElement) => {
        if (input.checked) {
          input.checked = false;
        }
      });
    }

    // emit event to clear or select all in reducer
    this.usersToggleAll.emit(checked);
  }

  public clearSort(): void {
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
  }

  sortData(sort: Sort): void {
    const data = this.users.slice();
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    const isAsc = sort.direction === 'asc';

    data.sort((a, b) => {
      switch (sort.active) {
        case 'jobRole':
          return compare(a.jobRole, b.jobRole, isAsc);
        case 'lastName':
          return compare(a.lastName, b.lastName, isAsc);
        default:
          return 0;
      }
    });

    this.sortedData = data;
  }
}

function compare(a: string, b: string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
